// components/portal/TableStatsPanel.tsx
'use client';

import { memo, useMemo } from 'react';
import clsx from 'clsx';
import { InventoryStatsEntry } from '#types/types';
import { AlertCircle, Loader2 } from '@/components/ui/lucide-memo';
import { toCountryFlag } from '#lib/leasing/brandDisplay';
import { useKernelStore } from '@/kernel/kernelStores';

interface TableStatsPanelProps {
  statsEntry: InventoryStatsEntry | null;
  onRetry: () => void;
}

const TONE_CLASS_BY_KEY: Record<string, string> = {
  neutral: 'border-white/10 bg-white/[0.02]',
  info: 'border-sky-400/25 bg-sky-500/[0.08]',
  success: 'border-emerald-400/25 bg-emerald-500/[0.08]',
  warning: 'border-amber-400/25 bg-amber-500/[0.08]',
  danger: 'border-rose-400/25 bg-rose-500/[0.08]',
};

const numberFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 1,
});

const parseBrandsCountFromLabel = (label: string): number | null => {
  const match = label.match(/\((\d+)\s+brands\)/i);
  if (!match) return null;
  const parsed = Number(match[1]);
  return Number.isFinite(parsed) ? parsed : null;
};

const toFiniteNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

const formatCompactNumber = (value: unknown): string => {
  const parsed = toFiniteNumber(value);
  if (parsed === null) return String(value);
  return numberFormatter.format(parsed);
};

function TableStatsPanelInner({ statsEntry, onRetry }: TableStatsPanelProps) {
  const isAdmin = useKernelStore((state) => state.userContext.isFaterAdmin);
  const content = useMemo(() => {
    if (!statsEntry || (statsEntry.isLoading && !statsEntry.data)) {
      return (
        <div className="flex min-h-[260px] flex-col items-center justify-center gap-3 text-white/40">
          <Loader2 size={26} className="animate-spin" />
          <span className="text-sm">Loading stats...</span>
        </div>
      );
    }

    if (!statsEntry.data && statsEntry.error) {
      return (
        <div className="flex min-h-[260px] flex-col items-center justify-center gap-3 text-center text-white/55">
          <AlertCircle size={24} className="text-amber-300" />
          <span className="text-sm">{statsEntry.error}</span>
          <button
            type="button"
            onClick={onRetry}
            className="inline-flex h-8 items-center rounded-[9px] bg-[#262626] px-3 text-[13px] text-white transition-colors hover:bg-[#303030]"
          >
            Retry
          </button>
        </div>
      );
    }

    const data = statsEntry.data;
    if (!data) {
      return (
        <div className="flex min-h-[220px] items-center justify-center text-sm text-white/35">
          Stats unavailable.
        </div>
      );
    }

    return (
      <div className="space-y-4 p-4">
        <div className="flex flex-wrap items-center gap-2 text-[11px] text-white/45">
          <span className="rounded-full border border-white/10 bg-white/[0.03] px-2 py-0.5">
            Scope: {data.scopeKey}
          </span>
          <span className="rounded-full border border-white/10 bg-white/[0.03] px-2 py-0.5">
            {new Date(data.computedAt).toLocaleString()}
          </span>
          <span className="rounded-full border border-white/10 bg-white/[0.03] px-2 py-0.5">
            {Math.round(data.executionMs)} ms
          </span>
          {data.isPartial && (
            <span className="rounded-full border border-amber-300/30 bg-amber-500/[0.08] px-2 py-0.5 text-amber-300">
              Partial
            </span>
          )}
        </div>

        {data.cards.length > 0 && (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5">
            {data.cards.map((card) => {
              const toneKey = card.tone || 'neutral';
              const toneClass = TONE_CLASS_BY_KEY[toneKey] || TONE_CLASS_BY_KEY.neutral;
              return (
                <div key={card.id} className={clsx('min-h-[94px] rounded-[12px] border p-3', toneClass)}>
                  <div className="text-[12px] text-white/60">{card.label}</div>
                  <div className="mt-2 text-[24px] leading-none text-white tabular-nums">
                    {formatCompactNumber(card.value)}
                    {card.unit ? <span className="ml-1 text-[14px] text-white/50">{card.unit}</span> : null}
                  </div>
                  {card.hint ? <div className="mt-2 text-[11px] text-white/45">{card.hint}</div> : null}
                </div>
              );
            })}
          </div>
        )}

        {Array.isArray(data.sections) && data.sections.length > 0 && (
          <div className="space-y-3">
            {data.sections.map((section) => {
              if (section.id === 'revenue_coverage' && !isAdmin) return null;
              if (section.id === 'country_summary') {
                const countryRows = section.rows
                  .map((row, index) => {
                    const countryCode = row.key.trim().toUpperCase();
                    const brandsCount = parseBrandsCountFromLabel(row.label);
                    const storesCount = toFiniteNumber(row.value);
                    return {
                      row,
                      index,
                      countryCode,
                      flag: toCountryFlag(countryCode),
                      brandsCount: brandsCount ?? 0,
                      storesCount: storesCount ?? 0,
                    };
                  })
                  .filter((entry) => entry.countryCode.length > 0);

                return (
                  <div key={section.id} className="rounded-[12px] border border-white/10 bg-white/[0.015] p-3">
                    <div className="mb-2 text-[13px] font-medium text-white/85">{section.label}</div>
                    <div className="grid max-w-[1120px] grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-2">
                      {countryRows.map((entry) => {
                        return (
                          <div
                            key={entry.row.key}
                            className="rounded-[10px] border border-white/10 bg-white/[0.02] px-3 py-2"
                          >
                            <div className="flex items-center justify-between gap-3">
                              <div className="flex items-center gap-2">
                                <span className="inline-flex min-w-[18px] justify-center text-[11px] text-white/40">
                                  #{entry.index + 1}
                                </span>
                                <span className="text-[18px] leading-none" aria-hidden>
                                  {entry.flag || '--'}
                                </span>
                                <span className="text-[13px] font-medium text-white">{entry.countryCode}</span>
                              </div>
                              <div className="text-[16px] font-semibold leading-none text-white tabular-nums">
                                {formatCompactNumber(entry.storesCount)}
                              </div>
                            </div>

                            <div className="mt-1.5 flex items-center justify-between text-[11px] text-white/55">
                              <span>{formatCompactNumber(entry.brandsCount)} brands</span>
                              <span>stores</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              }

              return (
                <div key={section.id} className="rounded-[12px] border border-white/10 bg-white/[0.015] p-3">
                  <div className="mb-2 text-[13px] font-medium text-white/85">{section.label}</div>
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
                    {section.rows.map((row) => (
                      <div
                        key={row.key}
                        className="rounded-[9px] border border-white/10 bg-white/[0.02] px-3 py-2.5"
                      >
                        <div className="truncate text-[12px] text-white/60">{row.label}</div>
                        <div className="mt-1 text-[19px] leading-none text-white tabular-nums">
                          {formatCompactNumber(row.value)}
                          {row.unit ? <span className="ml-1 text-[12px] text-white/45">{row.unit}</span> : null}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }, [onRetry, statsEntry, isAdmin]);

  return <div className="min-h-[260px]">{content}</div>;
}

export const TableStatsPanel = memo(TableStatsPanelInner);
